import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import Header from './components/Header'
import Footer from './components/Footer'
import Hero from './components/Hero'
import Features from './components/Features'
import Testimonials from './components/Testimonials'
import CTA, { CTADark, CTALight } from './components/CTA'
import PrivacyPage from './pages/Privacy'
import TermsPage from './pages/Terms'
import CookiesPage from './pages/Cookies'

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

// Layout wrapper component for marketing pages
function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <ScrollToTop />
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  )
}

// Home page - Landing page with all sections
function HomePage() {
  return (
    <>
      <Hero />
      <Features />
      <Testimonials />
      <CTA />
    </>
  )
}

// Features page - Detailed features breakdown
function FeaturesPage() {
  return (
    <>
      <section className="py-20 sm:py-28 bg-gradient-to-br from-primary-700 via-primary-600 to-primary-700">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Platform Features
            </h1>
            <p className="mt-6 text-lg leading-8 text-primary-100 sm:text-xl">
              Discover how Clarantis transforms your compliance program with AI-powered automation and enterprise-grade capabilities.
            </p>
          </div>
        </div>
      </section>
      <Features />
      <CTADark />
    </>
  )
}

// Pricing page
function PricingPage() {
  return (
    <>
      <section className="py-20 sm:py-28 bg-gradient-to-br from-primary-700 via-primary-600 to-primary-700">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Simple, Transparent Pricing
            </h1>
            <p className="mt-6 text-lg leading-8 text-primary-100 sm:text-xl">
              No hidden fees. No per-module charges. Straightforward pricing that scales with your organization.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            {/* Enterprise pricing card */}
            <div className="rounded-3xl bg-navy-50 p-8 sm:p-12 ring-1 ring-navy-100">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-primary-700">Enterprise</h2>
                <p className="mt-4 text-navy-500">
                  Custom pricing tailored to your organization's size and compliance needs.
                </p>
                <div className="mt-8">
                  <span className="text-5xl font-bold text-primary-700">Custom</span>
                </div>
                <p className="mt-2 text-sm text-navy-400">Billed annually</p>
              </div>

              <ul className="mt-10 space-y-4">
                {[
                  'Unlimited users',
                  'All compliance frameworks (SOC 2, ISO 27001, HIPAA, PCI DSS, GDPR, CMMC)',
                  'AI-powered gap analysis and recommendations',
                  'Automated evidence collection',
                  'Continuous monitoring and alerting',
                  'Dedicated customer success manager',
                  'Custom integrations',
                  '24/7 priority support',
                  'SSO/SAML authentication',
                  'Advanced reporting and analytics',
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <svg
                      className="h-6 w-6 flex-shrink-0 text-accent-teal"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-navy-600">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10 text-center">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-700 px-8 py-4 text-base font-semibold text-white shadow-lg hover:bg-primary-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-700 focus:ring-offset-2"
                >
                  Contact Sales
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3" />
                  </svg>
                </a>
              </div>
            </div>

            {/* FAQ teaser */}
            <div className="mt-16 text-center">
              <p className="text-navy-500">
                Have questions about pricing?{' '}
                <a href="/contact" className="font-medium text-primary-600 hover:text-primary-700">
                  Contact our team
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
      <CTALight />
    </>
  )
}

// About page
function AboutPage() {
  return (
    <>
      <section className="py-20 sm:py-28 bg-gradient-to-br from-primary-700 via-primary-600 to-primary-700">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              About Clarantis
            </h1>
            <p className="mt-6 text-lg leading-8 text-primary-100 sm:text-xl">
              Built by security practitioners for security teams. We understand compliance because we've lived it.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold tracking-tight text-primary-700 sm:text-4xl">
              Our Mission
            </h2>
            <p className="mt-6 text-lg leading-8 text-navy-500">
              Clarantis was founded with a simple mission: to bring clarity to compliance. We believe that security and compliance shouldn't be roadblocks—they should be enablers that help organizations move faster with confidence.
            </p>
            <p className="mt-4 text-lg leading-8 text-navy-500">
              Our team has decades of combined experience in cybersecurity, including incident response, forensic investigations, and helping organizations navigate complex regulatory requirements. We've seen firsthand the challenges that security teams face, and we've built Clarantis to solve them.
            </p>

            <h2 className="mt-16 text-3xl font-bold tracking-tight text-primary-700 sm:text-4xl">
              Our Values
            </h2>
            <div className="mt-8 grid gap-8 sm:grid-cols-2">
              {[
                {
                  title: 'Practitioner-First',
                  description: 'We build for the people doing the work, not just the executives reviewing reports.',
                },
                {
                  title: 'Transparency',
                  description: 'Clear pricing, honest communication, and no hidden surprises.',
                },
                {
                  title: 'Continuous Improvement',
                  description: 'We iterate relentlessly to stay ahead of evolving compliance requirements.',
                },
                {
                  title: 'Customer Success',
                  description: "Your success is our success. We're partners in your compliance journey.",
                },
              ].map((value) => (
                <div key={value.title} className="rounded-xl bg-navy-50 p-6">
                  <h3 className="text-lg font-semibold text-primary-700">{value.title}</h3>
                  <p className="mt-2 text-navy-500">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <CTA />
    </>
  )
}

// Contact page
function ContactPage() {
  return (
    <>
      <section className="py-20 sm:py-28 bg-gradient-to-br from-primary-700 via-primary-600 to-primary-700">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Contact Us
            </h1>
            <p className="mt-6 text-lg leading-8 text-primary-100 sm:text-xl">
              Ready to transform your compliance program? Let's talk about how Clarantis can help.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl">
            <form className="space-y-6">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-primary-700">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    autoComplete="given-name"
                    className="mt-2 block w-full rounded-lg border border-navy-200 px-4 py-3 text-primary-700 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-primary-700">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    autoComplete="family-name"
                    className="mt-2 block w-full rounded-lg border border-navy-200 px-4 py-3 text-primary-700 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-primary-700">
                  Work Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  autoComplete="email"
                  className="mt-2 block w-full rounded-lg border border-navy-200 px-4 py-3 text-primary-700 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-primary-700">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  autoComplete="organization"
                  className="mt-2 block w-full rounded-lg border border-navy-200 px-4 py-3 text-primary-700 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Acme Inc."
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-primary-700">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  className="mt-2 block w-full rounded-lg border border-navy-200 px-4 py-3 text-primary-700 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Tell us about your compliance needs..."
                />
              </div>

              <div>
                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary-700 px-8 py-4 text-base font-semibold text-white shadow-lg hover:bg-primary-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-700 focus:ring-offset-2"
                >
                  Send Message
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3" />
                  </svg>
                </button>
              </div>
            </form>

            <div className="mt-12 border-t border-navy-100 pt-12">
              <div className="grid gap-8 sm:grid-cols-2">
                <div>
                  <h3 className="text-lg font-semibold text-primary-700">Email Us</h3>
                  <p className="mt-2 text-navy-500">
                    <a href="mailto:hello@clarantis.com" className="hover:text-primary-600">
                      hello@clarantis.com
                    </a>
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-primary-700">Schedule a Demo</h3>
                  <p className="mt-2 text-navy-500">
                    See Clarantis in action with a personalized walkthrough.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

// Main App Routes
function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <Layout>
            <HomePage />
          </Layout>
        }
      />
      <Route
        path="/features"
        element={
          <Layout>
            <FeaturesPage />
          </Layout>
        }
      />
      <Route
        path="/pricing"
        element={
          <Layout>
            <PricingPage />
          </Layout>
        }
      />
      <Route
        path="/about"
        element={
          <Layout>
            <AboutPage />
          </Layout>
        }
      />
      <Route
        path="/contact"
        element={
          <Layout>
            <ContactPage />
          </Layout>
        }
      />
      <Route
        path="/privacy"
        element={
          <Layout>
            <PrivacyPage />
          </Layout>
        }
      />
      <Route
        path="/terms"
        element={
          <Layout>
            <TermsPage />
          </Layout>
        }
      />
      <Route
        path="/cookies"
        element={
          <Layout>
            <CookiesPage />
          </Layout>
        }
      />
      {/* Catch-all redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return <AppRoutes />
}
